/**
 * New Zealand PAYE settings for the tax year currently used by the calculator.
 * Update this object (and the year phrases in index.html) when the rates change.
 *
 * 2026-27 tax year: 1 April 2026 to 31 March 2027.
 *
 * Sources checked against Inland Revenue:
 * - Income tax: https://www.ird.govt.nz/income-tax/income-tax-for-individuals/tax-codes-and-tax-rates-for-individuals/tax-rates-for-individuals
 * - ACC earners' levy: https://www.ird.govt.nz/acclevy
 * - Student loan: https://www.ird.govt.nz/repaying-my-student-loan-when-i-earn-salary-or-wages
 * - KiwiSaver: https://www.ird.govt.nz/kiwisaver-changes
 */

export interface IncomeTaxBracket {
  /** Inclusive upper bound of income taxed at this rate. Infinity for the top bracket. */
  upTo: number;
  /** Marginal rate as a decimal, for example 0.105 for 10.5%. */
  rate: number;
}

export interface KiwiSaverRateOption {
  percent: number;
  label: string;
  isDefault?: boolean;
  isTemporaryReduction?: boolean;
}

export interface NzTaxYear {
  label: string;
  periodLabel: string;
  incomeTax: {
    brackets: readonly IncomeTaxBracket[];
  };
  accEarnersLevy: {
    /** GST-inclusive rate as a decimal. */
    rate: number;
    maxLiableEarnings: number;
  };
  studentLoan: {
    repaymentRate: number;
    annualRepaymentThreshold: number;
  };
  kiwiSaver: {
    employerMinimumRatePercent: number;
    employeeRateOptions: readonly KiwiSaverRateOption[];
  };
}

export const CURRENT_NZ_TAX_YEAR: NzTaxYear = {
  label: '2026-27',
  periodLabel: '1 April 2026 to 31 March 2027',
  incomeTax: {
    brackets: [
      { upTo: 15_600, rate: 0.105 },
      { upTo: 53_500, rate: 0.175 },
      { upTo: 78_100, rate: 0.3 },
      { upTo: 180_000, rate: 0.33 },
      { upTo: Number.POSITIVE_INFINITY, rate: 0.39 },
    ],
  },
  accEarnersLevy: {
    rate: 0.0175,
    maxLiableEarnings: 156_641,
  },
  studentLoan: {
    repaymentRate: 0.12,
    annualRepaymentThreshold: 24_128,
  },
  kiwiSaver: {
    employerMinimumRatePercent: 3.5,
    employeeRateOptions: [
      { percent: 0, label: 'No KiwiSaver' },
      { percent: 3.5, label: '3.5% (default)', isDefault: true },
      { percent: 4, label: '4%' },
      { percent: 6, label: '6%' },
      { percent: 8, label: '8%' },
      { percent: 10, label: '10%' },
      { percent: 3, label: '3% (temporary rate reduction)', isTemporaryReduction: true },
    ],
  },
};

/** Format a decimal rate (0.105, 0.0175) as a percentage, without trailing zeros. */
export function formatTaxRate(rate: number): string {
  const percent = Math.round(rate * 10000) / 100;
  const text = Number.isInteger(percent) ? percent.toFixed(0) : percent.toFixed(2).replace(/0$/, '');
  return `${text}%`;
}

export function formatNzDollars(amount: number): string {
  return amount.toLocaleString('en-NZ');
}

export function incomeTaxBracketRows(
  year: NzTaxYear = CURRENT_NZ_TAX_YEAR,
): { range: string; rate: string }[] {
  return year.incomeTax.brackets.map((bracket, index, brackets) => {
    const previousCap = index === 0 ? 0 : brackets[index - 1].upTo;
    const from = index === 0 ? 0 : previousCap + 1;
    const range = Number.isFinite(bracket.upTo)
      ? `$${formatNzDollars(from)} - $${formatNzDollars(bracket.upTo)}`
      : `$${formatNzDollars(from)} and over`;
    return { range, rate: formatTaxRate(bracket.rate) };
  });
}

export function defaultKiwiSaverRatePercent(year: NzTaxYear = CURRENT_NZ_TAX_YEAR): number {
  const option = year.kiwiSaver.employeeRateOptions.find((item) => item.isDefault);
  if (!option) {
    throw new Error(`No default KiwiSaver rate configured for the ${year.label} tax year`);
  }
  return option.percent;
}
